import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import type { ApiResponse } from '@/types/api';
import { getEmptyAnalyticsData, type AnalyticsData, type PlatformName, type TimeRange } from '../mockAnalytics';

const PLATFORM_LABEL: Record<string, PlatformName> = {
  facebook: 'Messenger',
  messenger: 'Messenger',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  website: 'Website',
};

/**
 * Analytics is only *partially* backable today: the API's leads trends give us
 * real conversation totals, a lead-stage funnel, and per-platform conversion
 * rates. The rich visuals (per-platform message volume, AI response time, top
 * intents, sentiment, CSAT) need data we don't store yet — those keep the mock
 * shape. We start from the mock (so every chart stays populated) and OVERLAY
 * the real numbers where we have them; an empty DB falls back to full mock.
 */
export function useAnalytics(range: TimeRange) {
  return useQuery({
    queryKey: ['analytics', range],
    queryFn: async (): Promise<AnalyticsData> => {
      const base = getEmptyAnalyticsData(range);
      try {
        const [perfRes, ovRes] = await Promise.all([
          apiClient.get<ApiResponse<any>>(`/api/dashboard/analytics/performance?timeframe=${range}`).catch(() => null),
          apiClient.get<ApiResponse<any>>('/api/dashboard/overview').catch(() => null),
        ]);
        const perf = perfRes?.data?.data || {};
        const ov = ovRes?.data?.data || {};
        const leads = ov?.metrics?.leads || {};
        const total = leads.total || 0;

        const platformPerf: any[] = perf?.platformPerformance || [];
        const platforms = base.platforms.map((p) => {
          const match = platformPerf.find(
            (pp) => PLATFORM_LABEL[String(pp.platform).toLowerCase()] === p.platform,
          );
          return match
            ? { ...p, leads: match.totalLeads || 0, convRate: parseFloat(match.conversionRate) || 0 }
            : p;
        });

        const funnel = [
          { stage: 'Conversations', value: total },
          { stage: 'Engaged', value: (leads.new || 0) + (leads.inProgress || 0) + (leads.converted || 0) },
          { stage: 'Qualified', value: (leads.inProgress || 0) + (leads.converted || 0) },
          { stage: 'Converted', value: leads.converted || 0 },
        ];

        return {
          ...base,
          summary: { ...base.summary, totalConversations: total },
          platforms,
          funnel,
        };
      } catch {
        return base;
      }
    },
    staleTime: 60 * 1000,
  });
}
